import React from "react";
import { Button, SaveButton, SimpleForm, useNotify } from "react-admin";
import dataProvider from "server/dataProvider";
import { Grid } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MoneyInput } from "server/components/Fields/MoneyInput";
import { specialistRequests } from "api/specialistApi";
import { useNotifyError } from "utils/useNotifyError";
import { SpecializationTypes } from "utils/constants";
import { MonthlyOrAnnualFeeInputs, hasFee } from "../Partials/MonthlyOrAnnualFeeInputs";

const FEE_KEYS = [
  "practicalTrainingMonthlyFee",
  "practicalTrainingAnnualFee",
  "theoreticalTrainingMonthlyFee",
  "theoreticalTrainingAnnualFee",
  "stateExamFee",
];

const isFilled = value => value !== undefined && value !== null && value !== "";

// Exactly one of the monthly/annual pair must be greater than 0
const validateMonthlyOrAnnual = (values, errors, monthlyKey, annualKey) => {
  const hasMonthly = hasFee(values[monthlyKey]);
  const hasAnnual = hasFee(values[annualKey]);

  if (!hasMonthly && !hasAnnual) {
    errors[monthlyKey] = "Въведете месечна или годишна такса";
    errors[annualKey] = "Въведете месечна или годишна такса";
  } else if (hasMonthly && hasAnnual) {
    errors[monthlyKey] = "Въведете само месечна или само годишна такса";
    errors[annualKey] = "Въведете само месечна или само годишна такса";
  }
};

// Form-level validation re-runs for all fields on every change, so the sibling's error clears too
const validateFees = isClinical => values => {
  const errors = {};

  if (!isClinical) {
    validateMonthlyOrAnnual(values, errors, "practicalTrainingMonthlyFee", "practicalTrainingAnnualFee");
  }
  validateMonthlyOrAnnual(values, errors, "theoreticalTrainingMonthlyFee", "theoreticalTrainingAnnualFee");

  if (!isFilled(values.stateExamFee)) {
    errors.stateExamFee = "Задължително";
  }

  FEE_KEYS.forEach(key => {
    if (isFilled(values[key]) && values[key] < 0 && !errors[key]) {
      errors[key] = "Стойността трябва да е поне 0";
    }
  });

  return errors;
};

const SpecializationsFeesEditForm = ({ id, specializationType, setIsEditing, fetchFees }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const isClinical = specializationType === SpecializationTypes.Clinical;

  const handleSave = async data => {
    const payload = {
      practicalTrainingMonthlyFee: isClinical ? null : (data.practicalTrainingMonthlyFee ?? null),
      practicalTrainingAnnualFee: isClinical ? null : (data.practicalTrainingAnnualFee ?? null),
      theoreticalTrainingMonthlyFee: data.theoreticalTrainingMonthlyFee ?? null,
      theoreticalTrainingAnnualFee: data.theoreticalTrainingAnnualFee ?? null,
      stateExamFee: data.stateExamFee,
    };
    try {
      await dataProvider.customRequest(specialistRequests.updateSpecializationFees(id), payload, "POST");
      fetchFees();
      notify("Таксите за специализации са обновени успешно", { type: "success" });
      setIsEditing(false);
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <>
      <SimpleForm
        toolbar={false}
        onSubmit={handleSave}
        validate={validateFees(isClinical)}
      >
        <Grid
          container
          spacing={2}
        >
          {!isClinical && (
            <MonthlyOrAnnualFeeInputs
              monthlySource="practicalTrainingMonthlyFee"
              annualSource="practicalTrainingAnnualFee"
              monthlyLabel="Месечна такса за практическо обучение"
              annualLabel="Годишна такса за практическо обучение"
            />
          )}
          <MonthlyOrAnnualFeeInputs
            monthlySource="theoreticalTrainingMonthlyFee"
            annualSource="theoreticalTrainingAnnualFee"
            monthlyLabel="Месечна такса за теоретично обучение"
            annualLabel="Годишна такса за теоретично обучение"
          />
          <Grid
            item
            xs={12}
            md={6}
          >
            <MoneyInput
              source="stateExamFee"
              label="Такса за държавен изпит"
            />
          </Grid>
        </Grid>
        <Grid sx={{ display: "flex", gap: 3, mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            color="error"
            size="small"
            label="Откажи"
            onClick={() => setIsEditing(false)}
          />
          <SaveButton
            label="Запази"
            size="small"
          />
        </Grid>
      </SimpleForm>
    </>
  );
};

export default SpecializationsFeesEditForm;
